/**
 * ドメイン限定ファクトチェック履歴サービス
 * Firestoreに直接アクセスして履歴を管理
 */
import {
  collection,
  deleteDoc,
  doc,
  limit as firestoreLimit,
  getDocs,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  DomainFactCheckHistoryItem,
  DomainVerificationResult,
  TavilyTimeRange,
} from '../types';

// ===== コレクション定数 =====
const USERS_COLLECTION = 'users';
const HISTORY_SUBCOLLECTION = 'domainFactCheckHistory';

// ===== ヘルパー関数 =====
const generateHistoryId = (): string => {
  return `dfch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ===== 履歴 CRUD =====

/**
 * 履歴を保存
 */
export const saveDomainFactCheckHistory = async (
  userId: string,
  data: {
    claim: string;
    query: string;
    user_input: string;
    domain_list_ids: string[];
    time_range: TavilyTimeRange;
    result: DomainVerificationResult;
  },
): Promise<string> => {
  try {
    const historyId = generateHistoryId();
    const now = new Date().toISOString();

    const historyItem: DomainFactCheckHistoryItem = {
      id: historyId,
      user_id: userId,
      claim: data.claim,
      query: data.query,
      user_input: data.user_input,
      domain_list_ids: data.domain_list_ids,
      time_range: data.time_range,
      result: data.result,
      created_at: now,
    };

    const historyRef = doc(db, USERS_COLLECTION, userId, HISTORY_SUBCOLLECTION, historyId);
    await setDoc(historyRef, historyItem);

    return historyId;
  } catch (error) {
    console.error('Error saving domain factcheck history:', error);
    throw new Error('履歴の保存に失敗しました');
  }
};

/**
 * 履歴一覧を取得（新しい順）
 */
export const getDomainFactCheckHistory = async (
  userId: string,
  maxItems: number = 50,
): Promise<DomainFactCheckHistoryItem[]> => {
  try {
    const historyRef = collection(db, USERS_COLLECTION, userId, HISTORY_SUBCOLLECTION);
    const q = query(historyRef, orderBy('created_at', 'desc'), firestoreLimit(maxItems));

    const snapshot = await getDocs(q);
    const history: DomainFactCheckHistoryItem[] = [];

    snapshot.forEach((docSnap) => {
      history.push(docSnap.data() as DomainFactCheckHistoryItem);
    });

    return history;
  } catch (error) {
    console.error('Error getting domain factcheck history:', error);
    throw new Error('履歴の取得に失敗しました');
  }
};

/**
 * 履歴を削除
 */
export const deleteDomainFactCheckHistory = async (
  userId: string,
  historyId: string,
): Promise<void> => {
  try {
    const historyRef = doc(db, USERS_COLLECTION, userId, HISTORY_SUBCOLLECTION, historyId);
    await deleteDoc(historyRef);
  } catch (error) {
    console.error('Error deleting domain factcheck history:', error);
    throw new Error('履歴の削除に失敗しました');
  }
};

/**
 * 日付をフォーマット（表示用）
 */
export const formatHistoryDate = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'たった今';
  } else if (diffMins < 60) {
    return `${diffMins}分前`;
  } else if (diffHours < 24) {
    return `${diffHours}時間前`;
  } else if (diffDays < 7) {
    return `${diffDays}日前`;
  } else {
    // MM/DD HH:mm 形式
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const mins = date.getMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${mins}`;
  }
};
